// Overlay para o modo "Capturar para Mockup" disparado por Alt+T (admin only).
// Ao ativar: destaca o container sob o cursor com outline azul. Click captura.
// Esc cancela. Captura abre /mockup-builder.html?import=<base64> em nova aba.
import { useEffect, useRef, useState } from 'react';
import { capturarParaMockup, abrirNoMockupBuilder, acharContainerSignificativo } from '../lib/captura-mockup';

interface Props {
  ativo: boolean;
  onFechar: () => void;
}

export function MockupCaptureOverlay({ ativo, onFechar }: Props) {
  const [alvo, setAlvo] = useState<HTMLElement | null>(null);
  // Highlight visual: posiciona um div absoluto por cima do alvo
  const highlightRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ativo) {
      setAlvo(null);
      return;
    }

    function elementoSobCursor(x: number, y: number): HTMLElement | null {
      // elementsFromPoint: pega lista do mais visivel pra menos. Pula o overlay/highlight.
      const lista = document.elementsFromPoint(x, y) as HTMLElement[];
      for (const el of lista) {
        if (el.hasAttribute('data-mockup-overlay')) continue;
        if (el.tagName === 'HTML' || el.tagName === 'BODY') continue;
        return el;
      }
      return null;
    }

    function alvoFinal(x: number, y: number): HTMLElement | null {
      const el = elementoSobCursor(x, y);
      if (!el) return null;
      // Promove pra modal-conteudo/dialog se aplicavel — captura header+body+footer juntos
      return acharContainerSignificativo(el);
    }

    function onMove(e: MouseEvent) {
      setAlvo(alvoFinal(e.clientX, e.clientY));
    }

    function onClick(e: MouseEvent) {
      e.preventDefault();
      e.stopPropagation();
      const el = alvoFinal(e.clientX, e.clientY);
      if (!el) return;
      try {
        const payload = capturarParaMockup(el);
        if (payload.elements.length === 0) {
          alert('Nada capturado. Tente outro container (modal, painel, formulario).');
          return;
        }
        const aberto = abrirNoMockupBuilder(payload);
        if (!aberto) {
          alert('Popup bloqueado pelo navegador. Permita popups para este site e tente de novo.');
          return;
        }
      } catch (err) {
        console.error('Erro ao capturar mockup:', err);
        alert('Erro ao capturar. Veja o console.');
      } finally {
        onFechar();
      }
    }

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onFechar();
      }
    }

    // Capture phase pra interceptar antes de qualquer onClick do app
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKey, true);
    document.body.style.cursor = 'crosshair';

    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKey, true);
      document.body.style.cursor = '';
    };
  }, [ativo, onFechar]);

  // Atualiza posicao do highlight + tooltip
  useEffect(() => {
    const hl = highlightRef.current;
    const tt = tooltipRef.current;
    if (!hl || !tt) return;
    if (!ativo || !alvo) {
      hl.style.display = 'none';
      tt.style.display = 'none';
      return;
    }
    const r = alvo.getBoundingClientRect();
    hl.style.display = 'block';
    hl.style.left = `${r.left}px`;
    hl.style.top = `${r.top}px`;
    hl.style.width = `${r.width}px`;
    hl.style.height = `${r.height}px`;

    tt.style.display = 'block';
    tt.style.left = `${r.left}px`;
    tt.style.top = `${Math.max(0, r.top - 28)}px`;
    const tag = alvo.tagName.toLowerCase();
    const cls = (alvo.className || '').toString().split(/\s+/).filter(Boolean).slice(0, 2).join('.');
    tt.textContent = `${tag}${cls ? '.' + cls : ''} — ${Math.round(r.width)}×${Math.round(r.height)}`;
  }, [ativo, alvo]);

  if (!ativo) return null;

  return (
    <>
      {/* Highlight: div azul translucido por cima do alvo */}
      <div
        ref={highlightRef}
        data-mockup-overlay="highlight"
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          background: 'rgba(59, 130, 246, 0.18)',
          border: '2px solid #2563eb',
          borderRadius: 4,
          zIndex: 99998,
          transition: 'all 60ms ease-out',
          display: 'none',
        }}
      />
      {/* Tooltip pequeno mostrando tag + dimensoes */}
      <div
        ref={tooltipRef}
        data-mockup-overlay="tooltip"
        style={{
          position: 'fixed',
          pointerEvents: 'none',
          background: '#1e3a8a',
          color: '#fff',
          padding: '3px 8px',
          borderRadius: 4,
          fontSize: 11,
          fontFamily: 'monospace',
          whiteSpace: 'nowrap',
          zIndex: 99999,
          display: 'none',
        }}
      />
      {/* Banner topo com instrucao */}
      <div
        data-mockup-overlay="banner"
        style={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#1e3a8a',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 99999,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          pointerEvents: 'none',
        }}
      >
        🎯 Modo captura — clique no container que quer capturar · Esc cancela
      </div>
    </>
  );
}
